import {
  useLocalBusinessConsent,
  storedBusinessRequest,
  persistBusinessRequest,
  isLocalConfirmationRequired,
  isDefiniteBusinessRejection,
} from '../../hooks/useLocalBusinessConsent';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import {
  ActionKey,
  type EntryChargeConfirm,
  type EntryDebtConfirm,
  type EntryDebtCollect,
  type EntryDebtStatement,
  type EntryDebtHistory,
  type EntryChargeBenefitOption,
  type EntryChargeInput,
  type EntryChargePreview,
  type EntryChargeRefund,
  type MemberChargeHistory,
  type MemberChargeReceipt,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';

type Pending =
  | { kind: 'PAYMENT'; body: EntryChargeConfirm }
  | { kind: 'DEBT_APPROVAL'; body: EntryDebtConfirm }
  | { kind: 'DEBT_COLLECTION'; id: string; body: EntryDebtCollect }
  | { kind: 'REFUND'; id: string; body: EntryChargeRefund };
export default function EntryCashierPanel(props: Parameters<typeof ScopedEntryCashierPanel>[0]) {
  const scope = useAuthStore((s) =>
    JSON.stringify([
      s.user?.id,
      sessionVenueKey(s.accessToken),
      s.sessionRevision,
      s.allowedActions,
      props.memberId,
      props.tournamentId,
    ]),
  );
  return <ScopedEntryCashierPanel key={scope} {...props} />;
}
function ScopedEntryCashierPanel({
  memberId,
  memberName,
  tournamentId,
  onClose,
}: {
  memberId: string;
  memberName: string;
  tournamentId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('tournament');
  const localBusiness = useLocalBusinessConsent();
  const inFlight = useRef(false);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const client = useQueryClient();
  const actions = useAuthStore((s) => s.allowedActions);
  const user = useAuthStore((s) => s.user);
  const can = (action: string) => !!user?.isSystemUser || !!actions?.includes(action);
  // Per-tab recovery survives navigation/reload; server still validates identity and payload.
  const recoveryKey = `member-charge-request:${user?.id}:${memberId}:${tournamentId}`;
  const recoveryScope = JSON.stringify([
    user?.id,
    sessionVenueKey(useAuthStore.getState().accessToken),
    memberId,
    tournamentId,
  ]);
  const [unreadable] = useState(() => {
    try {
      const saved = storedBusinessRequest<Pending>(recoveryKey);
      if (saved?.modern && saved.scope !== recoveryScope) return true;
      return (
        !!saved &&
        !(
          ['PAYMENT', 'REFUND', 'DEBT_APPROVAL', 'DEBT_COLLECTION'].includes(saved.body?.kind) &&
          typeof saved.body?.body?.operationKey === 'string'
        )
      );
    } catch {
      return true;
    }
  });
  const [pending, setPending] = useState<Pending | null>(() => {
    try {
      const value = storedBusinessRequest<Pending>(recoveryKey)?.body;
      if (
        value &&
        ['PAYMENT', 'REFUND', 'DEBT_APPROVAL', 'DEBT_COLLECTION'].includes(value.kind) &&
        typeof value.body?.operationKey === 'string'
      )
        return value;
    } catch {
      /* Corrupt local data is not an authoritative request. */
    }
    return null;
  });
  const [input, setInput] = useState<EntryChargeInput>(
    pending?.kind === 'PAYMENT' || pending?.kind === 'DEBT_APPROVAL'
      ? {
          tournamentId: pending.body.tournamentId,
          operation: pending.body.operation,
          tender: pending.body.tender,
          ...(pending.body.benefit ? { benefit: pending.body.benefit } : {}),
        }
      : { tournamentId, operation: 'ENTRY', tender: 'CASH' },
  );
  const [deferred, setDeferred] = useState(pending?.kind === 'DEBT_APPROVAL');
  const [debtReason, setDebtReason] = useState(
    pending?.kind === 'DEBT_APPROVAL' ? pending.body.reason : '',
  );
  const [debtCompleted, setDebtCompleted] = useState<EntryDebtStatement | null>(null);
  const [debtCursor, setDebtCursor] = useState<string | null>(null);
  const [confirmRefund, setConfirmRefund] = useState<string | null>(null);
  const [benefits, setBenefits] = useState<EntryChargeBenefitOption[]>([]);
  const [preview, setPreview] = useState<EntryChargePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [localMessage, setLocalMessage] = useState('');
  const [completed, setCompleted] = useState<MemberChargeReceipt | null>(null);
  const [reason, setReason] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const generation = useRef(0);
  const history = useQuery({
    queryKey: ['member-charges', memberId, tournamentId, cursor],
    queryFn: () =>
      api.get<MemberChargeHistory>(`/members/${memberId}/charges`, {
        params: { tournamentId, ...(cursor ? { cursor } : {}) },
      }),
    enabled: can(ActionKey.MEMBER_CHARGE_READ),
    retry: false,
  });
  const debts = useQuery({
    queryKey: ['member-entry-debts', memberId, tournamentId, debtCursor],
    queryFn: () =>
      api.get<EntryDebtHistory>(`/members/${memberId}/charges/debts`, {
        params: { tournamentId, ...(debtCursor ? { cursor: debtCursor } : {}) },
      }),
    enabled: can(ActionKey.MEMBER_CHARGE_READ),
    retry: false,
  });
  const locked = busy || !!pending || unreadable;
  function change(next: EntryChargeInput) {
    generation.current++;
    setInput(next);
    setPreview(null);
    setError('');
    setCompleted(null);
    setDebtCompleted(null);
  }
  async function loadPreview() {
    const revision = ++generation.current;
    setBusy(true);
    setError('');
    setPreview(null);
    setCompleted(null);
    try {
      const result = await api.post<EntryChargePreview>(
        `/members/${memberId}/charges/${deferred ? 'debts/preview' : 'preview'}`,
        input,
      );
      if (revision === generation.current) {
        setPreview(result);
        setBenefits(result.benefits ?? []);
      }
    } catch {
      if (revision === generation.current) setError('loadError');
    } finally {
      setBusy(false);
    }
  }
  async function submit(request: Pending) {
    if (busy || inFlight.current || unreadable || !live.current) return;
    inFlight.current = true;
    setBusy(true);
    let modern = !pending;
    try {
      const stored = storedBusinessRequest<Pending>(recoveryKey);
      modern = !pending || !!stored?.modern;
      if (
        (!pending || stored?.amendable) &&
        (request.kind === 'PAYMENT' || request.kind === 'DEBT_APPROVAL')
      ) {
        const consent = await localBusiness.ask([memberId], 'ENTRY');
        if (!consent || !live.current) {
          inFlight.current = false;
          setBusy(false);
          return;
        }
        if (request.kind === 'PAYMENT')
          request = { ...request, body: { ...request.body, localInitializationConsent: consent } };
        else
          request = { ...request, body: { ...request.body, localInitializationConsent: consent } };
      }
      if (modern) persistBusinessRequest(recoveryKey, request, 'UNKNOWN', recoveryScope);
      else sessionStorage.setItem(recoveryKey, JSON.stringify(request));
    } catch {
      setError('recoveryUnavailable');
      inFlight.current = false;
      setBusy(false);
      return;
    }
    setPending(request);
    setBusy(true);
    setError('');
    setCompleted(null);
    setLocalMessage('');
    try {
      const path =
        request.kind === 'PAYMENT'
          ? 'confirm'
          : request.kind === 'DEBT_APPROVAL'
            ? 'debts/confirm'
            : request.kind === 'DEBT_COLLECTION'
              ? `debts/${request.id}/collect`
              : `${request.id}/refund`;
      const receipt = await api.post<MemberChargeReceipt | EntryDebtStatement>(
        `/members/${memberId}/charges/${path}`,
        request.body,
      );
      if (!live.current) return;
      sessionStorage.removeItem(recoveryKey);
      setPending(null);
      setPreview(null);
      setReason('');
      setConfirmRefund(null);
      if (request.kind === 'DEBT_APPROVAL') setDebtCompleted(receipt as EntryDebtStatement);
      else setCompleted(receipt as MemberChargeReceipt);
      void client.invalidateQueries({ queryKey: ['member-entry-debts', memberId, tournamentId] });
      void client.invalidateQueries({ queryKey: ['member-charges', memberId, tournamentId] });
      void client.invalidateQueries({ queryKey: ['tournament', tournamentId] });
      void client.invalidateQueries({ queryKey: ['tournament-entries', tournamentId] });
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (isLocalConfirmationRequired(error)) {
        if (modern) persistBusinessRequest(recoveryKey, request, 'KNOWN_REJECTED', recoveryScope);
        setError('');
        setLocalMessage(modern ? localBusiness.reconfirmMessage : localBusiness.legacyMessage);
      } else if (modern && !pending && isDefiniteBusinessRejection(error, 'ENTRY')) {
        sessionStorage.removeItem(recoveryKey);
        setPending(null);
        setPreview(null);
        setError('stale');
      } else if (!modern && isDefiniteBusinessRejection(error, 'ENTRY')) {
        setError('');
        setLocalMessage(localBusiness.legacyMessage);
      } else setError(status === 403 ? 'permissionRecovery' : 'uncertain');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  function receiptDetails(receipt: MemberChargeReceipt) {
    return (
      <div className="text-sm space-y-1">
        {receipt.operation && (
          <p>
            {t('cashier.operation')}: {t(`cashier.${receipt.operation}`)}
          </p>
        )}
        <p>
          {t('cashier.principal')}: {receipt.principalAmount} · {t('cashier.venueFee')}:{' '}
          {receipt.venueFeeAmount}
        </p>
        <p>
          {t('cashier.reduction')}: {receipt.reductionAmount}
          {receipt.venueFeeDue !== undefined && (
            <>
              {' '}
              · {t('cashier.venueDue')}: {receipt.venueFeeDue}
            </>
          )}
        </p>
        {receipt.restoration?.items.map((item) => (
          <p key={item.kind}>
            {t(
              `cashier.${item.kind === 'RESERVATION' ? 'reservationRestored' : item.expired ? 'benefitRestoredExpired' : 'benefitRestored'}`,
              { benefit: t(`cashier.${item.kind}`) },
            )}
          </p>
        ))}
        {receipt.restoration?.monthlyPlanExcluded && <p>{t('cashier.monthlyNotRestored')}</p>}
        {receipt.correctedAt && !receipt.restoration && <p>{t('cashier.restorationUnknown')}</p>}
        {(receipt.feeVersionId || receipt.benefit) && (
          <details>
            <summary>{t('cashier.priceReferences')}</summary>
            {receipt.feeVersionId && (
              <p className="break-all">
                {t('cashier.feeVersion')}: {receipt.feeVersionId}
              </p>
            )}
            {receipt.benefit && (
              <p className="break-all">
                {t(`cashier.${receipt.benefit.kind}`)}: {receipt.benefit.id} ·{' '}
                {t('cashier.benefitUse')}: {receipt.benefit.useId}
              </p>
            )}
          </details>
        )}
      </div>
    );
  }
  const canPay =
    !!preview?.postingAvailable &&
    !!preview.quoteHash &&
    !!preview.expiresAt &&
    Date.parse(preview.expiresAt) > Date.now() &&
    preview.blockers.length === 0 &&
    can(ActionKey.MEMBER_CHARGE_CONFIRM) &&
    (!deferred || (can(ActionKey.MEMBER_DEFERRED_ADMISSION_APPROVE) && !!debtReason.trim()));
  return (
    <section
      className="mb-6 rounded-xl border border-base-300 p-4 space-y-4"
      aria-label={t('cashier.title')}
    >
      {localBusiness.confirmation}
      {localMessage && <p role="alert">{localMessage}</p>}
      {unreadable && <p role="alert">{t('cashier.recoveryUnavailable')}</p>}
      <div className="flex justify-between gap-3">
        <h2 className="font-bold">
          {t('cashier.title')} — {memberName}
        </h2>
        <button type="button" disabled={locked} onClick={onClose}>
          {t('cashier.close')}
        </button>
      </div>
      <p className="text-sm">{t('cashier.description')}</p>
      <p className="text-sm">{t('cashier.reservationPriority')}</p>
      {can(ActionKey.MEMBER_DEFERRED_ADMISSION_APPROVE) && can(ActionKey.MEMBER_CHARGE_CONFIRM) && (
        <label className="block">
          <input
            type="checkbox"
            checked={deferred}
            disabled={locked}
            onChange={(event) => {
              setDeferred(event.target.checked);
              change({ tournamentId, operation: 'ENTRY', tender: 'CASH' });
            }}
          />{' '}
          {t('cashier.deferAdmission')}
        </label>
      )}
      {deferred && (
        <label className="block">
          {t('cashier.debtReason')}
          <input
            className="block border rounded p-2 bg-base-100"
            value={debtReason}
            maxLength={500}
            disabled={locked}
            onChange={(event) => setDebtReason(event.target.value)}
          />
          <span className="text-sm">{t('cashier.debtNote')}</span>
        </label>
      )}
      <div className="flex flex-wrap gap-4">
        <label>
          {t('cashier.operation')}
          <select
            className="block rounded border p-2 bg-base-100"
            value={input.operation}
            disabled={locked}
            onChange={(e) => {
              setBenefits([]);
              change({
                tournamentId,
                tender: input.tender,
                operation: e.target.value as EntryChargeInput['operation'],
              });
            }}
          >
            {(['ENTRY', 'REENTRY', 'REBUY', 'ADDON'] as const)
              .filter((value) => !deferred || value === 'ENTRY' || value === 'REENTRY')
              .map((value) => (
                <option key={value} value={value}>
                  {t(`cashier.${value}`)}
                </option>
              ))}
          </select>
        </label>
        <label>
          {t('cashier.tender')}
          <select
            className="block rounded border p-2 bg-base-100"
            value={input.tender}
            disabled={locked || deferred}
            onChange={(e) =>
              change({ ...input, tender: e.target.value as EntryChargeInput['tender'] })
            }
          >
            <option value="CASH">{t('cashier.CASH')}</option>
            <option value="ZIPA">ZIPA</option>
          </select>
        </label>
      </div>
      {(benefits.length > 0 || input.benefit) && (
        <label className="block">
          {t('cashier.benefit')}
          <select
            className="block rounded border p-2 bg-base-100"
            value={input.benefit ? `${input.benefit.kind}:${input.benefit.id}` : ''}
            disabled={locked}
            onChange={(event) => {
              const selected = benefits.find(
                (option) => `${option.kind}:${option.id}` === event.target.value,
              );
              change({
                ...input,
                benefit: selected ? { kind: selected.kind, id: selected.id } : undefined,
              });
            }}
          >
            <option value="">{t('cashier.noBenefit')}</option>
            {input.benefit &&
              !benefits.some(
                (option) => option.kind === input.benefit!.kind && option.id === input.benefit!.id,
              ) && (
                <option value={`${input.benefit.kind}:${input.benefit.id}`} disabled>
                  {t('cashier.BENEFIT_UNAVAILABLE')}
                </option>
              )}
            {benefits.map((option) => (
              <option
                key={`${option.kind}:${option.id}`}
                value={`${option.kind}:${option.id}`}
                disabled={!!option.blocker}
              >
                {t(`cashier.${option.kind}`)} · {option.label} ·{' '}
                {option.blocker ? t(`cashier.${option.blocker}`) : option.eligibleAmount}
              </option>
            ))}
          </select>
          <span className="text-sm">{t('cashier.benefitHelp')}</span>
        </label>
      )}
      <button
        type="button"
        className="rounded bg-primary text-primary-content p-2"
        disabled={locked}
        onClick={() => void loadPreview()}
      >
        {t('cashier.preview')}
      </button>
      {error && (
        <p role="alert" className="text-error">
          {t(`cashier.${error}`)}
        </p>
      )}
      {preview && (
        <div className="space-y-2">
          <p>
            {t('cashier.principal')}: {preview.principalAmount ?? '—'}
          </p>
          {preview.pricing && (
            <div className="text-sm space-y-1">
              <p>
                {t('cashier.ordinaryPrice')}: {preview.pricing.ordinaryVenueFeeAmount}
              </p>
              {preview.pricing.reservationVenueFeeAmount !== null && (
                <p>
                  {t('cashier.reservationPrice')}: {preview.pricing.reservationVenueFeeAmount}
                </p>
              )}
              {preview.pricing.afterBreakVenueFeeAmount !== null && (
                <p>
                  {t('cashier.afterBreakPrice')}: {preview.pricing.afterBreakVenueFeeAmount}
                </p>
              )}
              <p>{t(`cashier.${preview.pricing.phase}`)}</p>
            </div>
          )}
          {preview.amounts ? (
            <>
              <p>
                {t('cashier.venueFee')}: {preview.amounts.selectedVenueFeeAmount}
              </p>
              <p>
                {t('cashier.reduction')}: {preview.amounts.ticketReduction}
              </p>
              <p>
                {t('cashier.venueDue')}: {preview.amounts.venueFeeDue}
              </p>
              <p className="font-bold">
                {t(preview.postingAvailable ? 'cashier.total' : 'cashier.estimate')}:{' '}
                {preview.amounts.totalDue}
              </p>
              <p>
                {preview.amounts.reasons
                  .map((code) => t(`cashier.${code}`, { defaultValue: t('cashier.priceRule') }))
                  .join(' / ')}
              </p>
            </>
          ) : (
            <p>{t('cashier.priceUnavailable')}</p>
          )}
          {preview.blockers.map((code) => (
            <p key={code}>
              {t(`cashier.${code}`, { defaultValue: t('cashier.CHARGE_INTEGRATION_UNAVAILABLE') })}
            </p>
          ))}
          <button
            type="button"
            className="rounded bg-primary text-primary-content p-2"
            disabled={locked || !canPay}
            onClick={() => {
              if (preview.expiresAt && Date.parse(preview.expiresAt) <= Date.now()) {
                setPreview(null);
                setError('stale');
                return;
              }
              if (canPay && preview.quoteHash && deferred) {
                void submit({
                  kind: 'DEBT_APPROVAL',
                  body: {
                    ...input,
                    tender: 'CASH',
                    reason: debtReason.trim(),
                    quoteHash: preview.quoteHash,
                    operationKey: crypto.randomUUID(),
                  },
                });
              } else if (canPay && preview.quoteHash)
                void submit({
                  kind: 'PAYMENT',
                  body: {
                    ...input,
                    quoteHash: preview.quoteHash,
                    operationKey: crypto.randomUUID(),
                  },
                });
            }}
          >
            {t(deferred ? 'cashier.approveDebt' : 'cashier.pay')}
          </button>
        </div>
      )}
      {pending && !busy && (
        <button type="button" disabled={unreadable} onClick={() => void submit(pending)}>
          {t('cashier.retry')}
        </button>
      )}
      {completed && (
        <div role="status">
          <p>
            {t('cashier.saved')} {completed.id} · {completed.netDue} · {completed.tender}
          </p>
          {receiptDetails(completed)}
        </div>
      )}
      {debtCompleted && (
        <p role="status">
          {t('cashier.debtApproved')} {debtCompleted.id} · {debtCompleted.approvedDue}
        </p>
      )}
      {can(ActionKey.MEMBER_CHARGE_READ) && (
        <div className="space-y-2">
          <h3 className="font-semibold">{t('cashier.debts')}</h3>
          {debts.isError && <p role="alert">{t('cashier.loadError')}</p>}
          {debts.data && (
            <p>
              {t('cashier.outstandingDue')}: {debts.data.outstandingDue}
            </p>
          )}
          {debts.data?.items.map((debt) => (
            <article key={debt.id} className="border rounded p-3 space-y-2">
              <p>
                {debt.approvedDue} · {t(`cashier.${debt.state}`)}
              </p>
              <p className="text-sm break-all">
                {t('cashier.approver')}: {debt.approverId} ·{' '}
                {new Date(debt.approvedAt).toLocaleString()}
              </p>
              {debt.state === 'APPROVED_UNPAID' &&
                can(ActionKey.MEMBER_DEFERRED_PAYMENT_COLLECT) && (
                  <button
                    type="button"
                    disabled={locked}
                    onClick={() =>
                      void submit({
                        kind: 'DEBT_COLLECTION',
                        id: debt.id,
                        body: { operationKey: crypto.randomUUID(), tender: 'CASH' },
                      })
                    }
                  >
                    {t('cashier.collectDebt', { amount: debt.approvedDue })}
                  </button>
                )}
            </article>
          ))}
          {(debtCursor || debts.data?.nextCursor) && (
            <div className="flex gap-4">
              <button
                type="button"
                disabled={locked || !debtCursor}
                onClick={() => setDebtCursor(null)}
              >
                {t('cashier.firstPage')}
              </button>
              <button
                type="button"
                disabled={locked || !debts.data?.nextCursor}
                onClick={() => setDebtCursor(debts.data?.nextCursor ?? null)}
              >
                {t('cashier.nextPage')}
              </button>
            </div>
          )}
        </div>
      )}
      <h3 className="font-semibold">{t('cashier.receipts')}</h3>
      <p className="text-sm">{t('cashier.historyNote')}</p>
      {history.isPending && <p>{t('cashier.loading')}</p>}
      {history.isError && <p role="alert">{t('cashier.loadError')}</p>}
      {history.data?.items.length === 0 && <p>{t('cashier.empty')}</p>}
      {history.data?.items.map((row) => (
        <article key={row.id} className="border rounded p-3 space-y-2">
          <p className="break-all">{row.id}</p>
          <p>
            {row.netDue} · {row.tender ? t(`cashier.${row.tender}`) : '—'} ·{' '}
            {t(`cashier.${row.status}`, { defaultValue: row.status })}
          </p>
          {receiptDetails(row)}
          <p>
            {t('cashier.paidAt')}:{' '}
            {row.paidAt
              ? new Date(row.paidAt).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })
              : '—'}
          </p>
          {row.correctedAt && (
            <p>
              {t('cashier.correctedAt')}:{' '}
              {new Date(row.correctedAt).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })}
            </p>
          )}
          {row.purpose === 'ENTRY' &&
            row.status === 'POSTED' &&
            !row.correctedAt &&
            can(ActionKey.MEMBER_CHARGE_REFUND) && (
              <button
                type="button"
                disabled={locked || !row.refundable || !reason.trim()}
                onClick={() =>
                  confirmRefund !== row.id
                    ? setConfirmRefund(row.id)
                    : void submit({
                        kind: 'REFUND',
                        id: row.id,
                        body: { reason: reason.trim(), operationKey: crypto.randomUUID() },
                      })
                }
              >
                {t(confirmRefund === row.id ? 'cashier.confirmRefundUnseat' : 'cashier.refund')}
              </button>
            )}
        </article>
      ))}
      {can(ActionKey.MEMBER_CHARGE_REFUND) && (
        <label className="block">
          {t('cashier.refundReason')}
          <input
            className="block border rounded p-2 bg-base-100"
            value={reason}
            maxLength={500}
            disabled={locked}
            onChange={(e) => setReason(e.target.value)}
          />
          <span className="block text-sm">{t('cashier.refundNote')}</span>
        </label>
      )}
      {(cursor || history.data?.nextCursor) && (
        <div className="flex gap-4">
          <button disabled={locked || !cursor} onClick={() => setCursor(null)}>
            {t('cashier.firstPage')}
          </button>
          <button
            disabled={locked || !history.data?.nextCursor}
            onClick={() => setCursor(history.data?.nextCursor ?? null)}
          >
            {t('cashier.nextPage')}
          </button>
        </div>
      )}
    </section>
  );
}
